import { useMemo, type ReactNode } from 'react';

import type { SearchFilterOption } from '../SearchFilterSelect';
import { SearchFilterSelect } from '../SearchFilterSelect';
import { Field } from './Field';
import { TilePreview, type TilePreviewProps } from './TilePreview';

import styles from './entitySelector.module.scss';

const CREATE_NEW_VALUE = '__create__';

export type EntitySelectorOption<T = unknown> = {
  /** Unique identifier for the option */
  id: number | string;
  /** Display label in the dropdown */
  label: string;
  /** Optional metadata for the option */
  meta?: T;
};

export type EntitySelectorProps<T = unknown> = {
  /**
   * Label for the selector field
   */
  label?: string;
  /**
   * Currently selected value (id)
   */
  value: number | string | null;
  /**
   * List of available options
   */
  options: EntitySelectorOption<T>[];
  /**
   * Called when selection changes
   */
  onChange: (option: EntitySelectorOption<T> | null) => void;
  /**
   * Placeholder text when nothing is selected
   */
  placeholder?: string;
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text shown below the field
   */
  helperText?: string;
  /**
   * Whether options are loading
   */
  loading?: boolean;
  /**
   * Loading placeholder text
   */
  loadingText?: string;
  /**
   * Optional callback to create a new entity
   */
  onCreateClick?: () => void;
  /**
   * Label for the create new option
   */
  createLabel?: string;
  /**
   * Whether to show an empty-state create placeholder tile
   */
  showCreatePlaceholder?: boolean;
  /**
   * Label for the create placeholder tile
   */
  createPlaceholderLabel?: string;
  /**
   * Whether to enable search in the dropdown
   */
  searchEnabled?: boolean;
  /**
   * Whether to open the dropdown when the input receives focus. Default: true
   */
  openOnFocus?: boolean;
  /**
   * Render function for the preview tile when an option is selected.
   * If not provided, no preview is shown.
   */
  renderPreview?: (option: EntitySelectorOption<T>) => TilePreviewProps;
  /**
   * Additional class name for the container
   */
  className?: string;
  /**
   * Children to render (for custom preview content)
   */
  children?: (option: EntitySelectorOption<T>) => ReactNode;
};

/**
 * EntitySelector - A composable selector for choosing entities with optional preview.
 *
 * Combines a searchable dropdown with an optional tile preview of the selected item.
 * Used as a building block for domain-specific selectors like ParentSelector and LitterSelector.
 *
 * @example
 * ```tsx
 * <EntitySelector
 *   label="Select Dog"
 *   value={selectedId}
 *   options={dogs.map(d => ({ id: d.id, label: d.name, meta: d }))}
 *   onChange={(opt) => setSelectedId(opt?.id ?? null)}
 *   renderPreview={(opt) => ({
 *     itemType: 'breedingDog',
 *     imageUrl: opt.meta?.imageUrl,
 *     title: opt.label,
 *     meta: opt.meta?.sex
 *   })}
 * />
 * ```
 */
export function EntitySelector<T = unknown>({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  required = false,
  error,
  helperText,
  loading = false,
  loadingText = 'Loading...',
  onCreateClick,
  createLabel = '+ Add new',
  showCreatePlaceholder = false,
  createPlaceholderLabel,
  searchEnabled = true,
  openOnFocus = true,
  renderPreview,
  className,
  children
}: EntitySelectorProps<T>) {
  const selectOptions = useMemo((): SearchFilterOption[] => {
    const mapped = options.map((opt) => ({
      value: String(opt.id),
      label: opt.label
    }));

    if (onCreateClick) {
      mapped.push({
        value: CREATE_NEW_VALUE,
        label: createLabel
      });
    }

    return mapped;
  }, [options, onCreateClick, createLabel]);

  const handleSelect = (selectedValue: string | undefined) => {
    if (selectedValue === CREATE_NEW_VALUE && onCreateClick) {
      onCreateClick();
      return;
    }

    if (!selectedValue) {
      onChange(null);
      return;
    }

    const option = options.find((o) => String(o.id) === selectedValue);
    onChange(option ?? null);
  };

  const handleClear = () => {
    onChange(null);
  };

  const selectedOption = options.find((o) => String(o.id) === String(value));
  const resolvedCreatePlaceholderLabel =
    createPlaceholderLabel ?? createLabel.replace(/^\+\s*/, '');
  const isCreatePlaceholderVisible =
    Boolean(onCreateClick) && showCreatePlaceholder && !selectedOption;

  return (
    <div className={`${styles.entitySelector} ${className || ''}`}>
      <Field
        label={label}
        required={required}
        helpText={helperText}
        error={error}
      >
        <SearchFilterSelect
          variant="field"
          options={selectOptions}
          value={value !== null ? String(value) : undefined}
          placeholder={loading ? loadingText : placeholder}
          onSelect={disabled || loading ? undefined : handleSelect}
          onClear={disabled || loading ? undefined : handleClear}
          searchEnabled={searchEnabled}
          openOnFocus={openOnFocus}
          allowClear
        />
      </Field>

      {isCreatePlaceholderVisible && onCreateClick && (
        <button
          type="button"
          className={styles.createPlaceholder}
          onClick={onCreateClick}
          disabled={disabled || loading}
        >
          <span className={styles.createPlaceholderIcon}>+</span>
          <span className={styles.createPlaceholderLabel}>
            {resolvedCreatePlaceholderLabel}
          </span>
        </button>
      )}

      {selectedOption && renderPreview && (
        <div className={styles.preview}>
          <TilePreview {...renderPreview(selectedOption)}>
            {children?.(selectedOption)}
          </TilePreview>
        </div>
      )}
    </div>
  );
}
